import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { EMPTY, of } from "rxjs";
import {
  catchError,
  debounceTime,
  exhaustMap,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs/operators";

import saveAs from "file-saver";

import * as NotificationActions from "../../notifications/actions/notification.actions";

import * as SearchBySkillOrConsultantApiActions from "@skills-app/consultant-skill/actions/search-by-skill-or-consultant-api.actions";
import * as SearchBySkillOrConsultantPageActions from "@skills-app/consultant-skill/actions/search-by-skill-or-consultant.actions";
import { BuildTeamPageActions } from "@skills-app/build-team/actions";
import { SkillChampionPageActions } from "@skills-app/thingspace/actions";
import { ConsultantSearchService } from "@skills-app/consultant-skill/services/consultant-search.service";
import * as fromConsultantSearchReducer from "@skills-app/consultant-skill/reducers/consultant-search.reducer";
import { AppState } from "@skills-app/root.reducer";
import IConsultantSearch from "@skills-app/models/consultant-search";
import IOrgNode from "@skills-app/models/orgNode";
import { ISkillNode } from "@skills-app/models/skillNode";
import { IIndustryItem } from "@skills-app/models/industry";
import { ICapability } from "@skills-app/models/capability";
import ICertification from "@skills-app/models/certification";

/**
 * Backend returns OrganisationDTO[] for /api/UserSkills/Orgs:
 *  - Id, Name, Hierarchy: string[] (path of org ids), ManagerId
 * Frontend expects IOrgNode tree:
 *  - Id, Name, Hierarchy: string, Children: IOrgNode[]
 */
type OrganisationDTO = {
  Id: string;
  Name: string;
  Hierarchy?: string[];
  ManagerId?: string;
};

function isOrgNodeTree(x: any): x is IOrgNode[] {
  return (
    Array.isArray(x) &&
    (x.length === 0 ||
      (x[0] &&
        typeof x[0] === "object" &&
        "Children" in (x[0] as Record<string, unknown>)))
  );
}

function toHierarchyString(path: string[] | undefined): string {
  const arr = path ?? [];
  return arr.join(" > ");
}

function buildOrgTree(dtos: OrganisationDTO[]): IOrgNode[] {
  const uniqDtos = Array.from(
    new Map(
      (dtos ?? []).filter((d) => d?.Id).map((d) => [d.Id, d] as const),
    ).values(),
  );

  const byId = new Map<string, IOrgNode>();

  for (const dto of uniqDtos) {
    if (!dto?.Id) continue;
    byId.set(dto.Id, {
      Id: dto.Id,
      Name: dto.Name ?? "",
      Hierarchy: toHierarchyString(dto.Hierarchy),
      Children: [],
    });
  }

  const roots: IOrgNode[] = [];
  for (const dto of uniqDtos) {
    if (!dto?.Id) continue;
    const node = byId.get(dto.Id);
    if (!node) continue;

    const path = dto.Hierarchy ?? [];
    if (path.length === 0) {
      roots.push(node);
      continue;
    }

    const last = path[path.length - 1];
    const parentId =
      last === dto.Id
        ? path.length >= 2
          ? path[path.length - 2]
          : null
        : last;

    if (!parentId || parentId === dto.Id) {
      roots.push(node);
      continue;
    }

    const parent = byId.get(parentId);
    if (!parent) {
      roots.push(node);
      continue;
    }

    parent.Children = parent.Children ?? [];
    if (!parent.Children.some((c) => c.Id === node.Id)) {
      parent.Children.push(node);
    }
  }

  const rootMap = new Map<string, IOrgNode>();
  for (const r of roots) rootMap.set(r.Id, r);

  const sortTree = (nodes: IOrgNode[]) => {
    nodes.sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? ""));
    for (const n of nodes) {
      if (n.Children?.length) sortTree(n.Children);
    }
  };

  const result = Array.from(rootMap.values());
  sortTree(result);
  return result;
}

function orgsToTree(orgs: unknown): IOrgNode[] {
  if (isOrgNodeTree(orgs)) {
    return (orgs as IOrgNode[]) ?? [];
  }

  const dtos = (orgs as OrganisationDTO[]) ?? [];
  return buildOrgTree(dtos);
}

/**
 * Parity rule:
 * - [] means "send no explicit org ids"
 * - HasOrganisationSelection distinguishes:
 *   - false => user unticked everything => return no results
 *   - true  => user has at least one org selected
 */
function normaliseSelectedOrganisationIds(
  selectedOrganisations: IOrgNode[] | null | undefined,
  allOrgsTree: IOrgNode[] | null | undefined,
): string[] {
  const selectedIds = Array.from(
    new Set(
      (selectedOrganisations ?? [])
        .map((o) => String(o?.Id ?? "").trim())
        .filter(Boolean),
    ),
  );

  if (selectedIds.length === 0) {
    return [];
  }

  return selectedIds;
}

function buildConsultantSearchCriteria(state: {
  selectedSkills: ISkillNode[];
  selectedIndustryItems: IIndustryItem[];
  selectedCapabilities: ICapability[];
  selectedCertifications: ICertification[];
  selectedOrganisations: IOrgNode[];
  allOrgs: IOrgNode[];
  selectedLocations: string[];
  selectedRoles: string[];
  anySearchTerm: string;
  consultantNameSearchTerm: string;
  showUnknownProficiency: boolean;
  includeRelatedSkills: boolean;
  includeExpiredCerts: boolean;
  isSkillComboLogicOR: boolean;
  isIndustryComboLogicOR: boolean;
  updatedWithinMonths: number | null;
  source: "Search" | "BuildTeam";
}): IConsultantSearch {
  const selectedOrganisationIds = normaliseSelectedOrganisationIds(
    state.selectedOrganisations ?? [],
    state.allOrgs ?? [],
  );

  const hasOrganisationSelection = selectedOrganisationIds.length > 0;

  return {
    SelectedSkills: state.selectedSkills ?? [],
    SelectedIndustries: state.selectedIndustryItems ?? [],
    SelectedCapabilities: state.selectedCapabilities ?? [],
    SelectedCertifications: state.selectedCertifications ?? [],
    SelectedOrganisations: [],
    SelectedOrganisationIds: selectedOrganisationIds,
    HasOrganisationSelection: hasOrganisationSelection,
    SelectedLocations: state.selectedLocations ?? [],
    Roles: state.selectedRoles ?? [],
    AnySearchTerm: state.anySearchTerm ?? "",
    ConsultantNameSearchTerm: state.consultantNameSearchTerm ?? "",
    ShowUnknownProficiency: state.showUnknownProficiency ?? true,
    IncludeRelatedSkills: state.includeRelatedSkills ?? false,
    IncludeExpiredCerts: state.includeExpiredCerts ?? false,
    IsSkillComboLogicOR: state.isSkillComboLogicOR ?? true,
    IsIndustryComboLogicOR: state.isIndustryComboLogicOR ?? true,
    UpdatedWithinMonths: state.updatedWithinMonths ?? null,
    Source: state.source,
  };
}

function tryExtractFilenameFromContentDisposition(
  contentDisposition: string | null,
): string | null {
  const cd = (contentDisposition ?? "").trim();
  if (!cd) return null;

  const filenameStarMatch = cd.match(/filename\*\s*=\s*([^;]+)/i);
  if (filenameStarMatch?.[1]) {
    const raw = filenameStarMatch[1].trim();
    const unquoted = raw.replace(/^"(.*)"$/, "$1");
    const parts = unquoted.split("''");
    const maybeEncoded = parts.length === 2 ? parts[1] : unquoted;
    try {
      return decodeURIComponent(maybeEncoded);
    } catch {
      return maybeEncoded;
    }
  }

  const filenameMatch = cd.match(/filename\s*=\s*([^;]+)/i);
  if (filenameMatch?.[1]) {
    const raw = filenameMatch[1].trim();
    return raw.replace(/^"(.*)"$/, "$1");
  }

  return null;
}

function buildCriteriaCacheKey(criteria: IConsultantSearch): string {
  const skillIds = (criteria.SelectedSkills ?? [])
    .map((s: any) => String(s?.Id ?? ""))
    .filter(Boolean)
    .sort();

  const industryKeys = (criteria.SelectedIndustries ?? [])
    .map((i: any) => String(i?.IndustryOptions ?? i?.Name ?? ""))
    .filter(Boolean)
    .sort();

  const capabilityIds = (criteria.SelectedCapabilities ?? [])
    .map((c: any) => String(c?.Id ?? c?.Name ?? ""))
    .filter(Boolean)
    .sort();

  const certificationIds = (criteria.SelectedCertifications ?? [])
    .map((c: any) => String(c?.Id ?? ""))
    .filter(Boolean)
    .sort();

  const orgIds = (criteria.SelectedOrganisationIds ?? [])
    .map((o) => String(o ?? ""))
    .filter(Boolean)
    .sort();

  const locations = (criteria.SelectedLocations ?? [])
    .map((l) => String(l ?? ""))
    .filter(Boolean)
    .sort();

  const roles = (criteria.Roles ?? [])
    .map((r) => String(r ?? ""))
    .filter(Boolean)
    .sort();

  return JSON.stringify({
    skillIds,
    industryKeys,
    capabilityIds,
    certificationIds,
    orgIds,
    locations,
    roles,
    anySearchTerm: (criteria.AnySearchTerm ?? "").trim(),
    consultantNameSearchTerm: (criteria.ConsultantNameSearchTerm ?? "").trim(),
    showUnknownProficiency: criteria.ShowUnknownProficiency ?? true,
    includeRelatedSkills: criteria.IncludeRelatedSkills ?? false,
    includeExpiredCerts: criteria.IncludeExpiredCerts ?? false,
    isSkillComboLogicOR: criteria.IsSkillComboLogicOR ?? true,
    isIndustryComboLogicOR: criteria.IsIndustryComboLogicOR ?? true,
    updatedWithinMonths: criteria.UpdatedWithinMonths ?? null,
    hasOrganisationSelection: criteria.HasOrganisationSelection ?? false,
    source: criteria.Source ?? "Search",
  });
}

@Injectable()
export class ConsultantSearchEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly consultantSearchService: ConsultantSearchService,
    private readonly store: Store<AppState>,
    private readonly router: Router,
  ) {}

  private lastRolesOrgKey = "";
  private lastLocationsOrgKey = "";
  private lastFilteredConsultantsCriteriaKey = "";

  private selectedOrgIds$ = this.store
    .select(fromConsultantSearchReducer.getSelectedOrgs)
    .pipe(map((orgs) => (orgs ?? []).map((o) => o.Id).filter(Boolean)));

  private getCurrentSearchSource(): "Search" | "BuildTeam" {
    const url = this.router.url ?? "";
    return url.startsWith("/build-team") ? "BuildTeam" : "Search";
  }

  resetFilteredCriteriaCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SearchBySkillOrConsultantPageActions.unloadPage,
          BuildTeamPageActions.unloadPage,
          SkillChampionPageActions.clearConsultantSearch,
        ),
        tap(() => {
          this.lastFilteredConsultantsCriteriaKey = "";
        }),
      ),
    { dispatch: false },
  );

  requestRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchBySkillOrConsultantApiActions.requestRoles),
      withLatestFrom(
        this.selectedOrgIds$,
        this.store.select(fromConsultantSearchReducer.getUserRoles),
      ),
      exhaustMap(([, orgIds, roles]) => {
        const orgKey = (orgIds ?? []).join("|");
        if ((roles?.length ?? 0) > 0 && orgKey === this.lastRolesOrgKey) {
          return EMPTY;
        }
        this.lastRolesOrgKey = orgKey;

        return this.consultantSearchService.getRoles(orgIds ?? []).pipe(
          map((r) =>
            SearchBySkillOrConsultantApiActions.requestRolesSuccess({
              roles: r,
            }),
          ),
          catchError(() =>
            of(
              SearchBySkillOrConsultantApiActions.requestRolesError(),
              NotificationActions.snackbarOpen({
                message: "Error: Unable to load roles.",
                action: "Dismiss",
              }),
            ),
          ),
        );
      }),
    ),
  );

  requestLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchBySkillOrConsultantApiActions.requestLocations),
      withLatestFrom(
        this.selectedOrgIds$,
        this.store.select(fromConsultantSearchReducer.getLocations),
      ),
      exhaustMap(([, orgIds, locations]) => {
        const orgKey = (orgIds ?? []).join("|");
        if (
          (locations?.length ?? 0) > 0 &&
          orgKey === this.lastLocationsOrgKey
        ) {
          return EMPTY;
        }
        this.lastLocationsOrgKey = orgKey;

        return this.consultantSearchService.getLocations(orgIds ?? []).pipe(
          map((locs) =>
            SearchBySkillOrConsultantApiActions.requestLocationsSuccess({
              locations: locs,
            }),
          ),
          catchError(() =>
            of(
              SearchBySkillOrConsultantApiActions.requestLocationsError(),
              NotificationActions.snackbarOpen({
                message: "Error: Unable to load locations.",
                action: "Dismiss",
              }),
            ),
          ),
        );
      }),
    ),
  );

  requestOrgs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SearchBySkillOrConsultantApiActions.requestOrgs,
        SearchBySkillOrConsultantApiActions.requestOrgsForce,
      ),
      withLatestFrom(this.store.select(fromConsultantSearchReducer.getOrgs)),
      exhaustMap(([action, orgs]) => {
        const isForce =
          action.type ===
          SearchBySkillOrConsultantApiActions.requestOrgsForce.type;
        if (!isForce && (orgs?.length ?? 0) > 0) return EMPTY;

        return this.consultantSearchService.getOrgs().pipe(
          map((rawOrgs: unknown) =>
            SearchBySkillOrConsultantApiActions.requestOrgsSuccess({
              orgs: orgsToTree(rawOrgs),
            }),
          ),
          catchError(() =>
            of(
              SearchBySkillOrConsultantApiActions.requestOrgsError(),
              NotificationActions.snackbarOpen({
                message: "Error: Unable to load organisations.",
                action: "Dismiss",
              }),
            ),
          ),
        );
      }),
    ),
  );

  requestFilteredConsultants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchBySkillOrConsultantApiActions.requestFilteredConsultants),
      withLatestFrom(
        this.store.select(fromConsultantSearchReducer.getSelectedSkills),
        this.store.select(fromConsultantSearchReducer.getSelectedIndustryItems),
        this.store.select(fromConsultantSearchReducer.getSelectedCapabilities),
        this.store.select(
          fromConsultantSearchReducer.getSelectedCertifications,
        ),
        this.store.select(fromConsultantSearchReducer.getSelectedOrgs),
        this.store.select(fromConsultantSearchReducer.getOrgs),
        this.store.select(fromConsultantSearchReducer.getSelectedLocations),
        this.store.select(fromConsultantSearchReducer.getSelectedRoles),
        this.store.select(fromConsultantSearchReducer.getAnySearchTerm),
        this.store.select(
          fromConsultantSearchReducer.getConsultantNameSearchTerm,
        ),
        this.store.select(
          fromConsultantSearchReducer.getShowUnknownProficiency,
        ),
        this.store.select(fromConsultantSearchReducer.getIncludeRelatedSkills),
        this.store.select(fromConsultantSearchReducer.getIncludeExpiredCerts),
        this.store.select(fromConsultantSearchReducer.getIsSkillComboLogicOR),
        this.store.select(
          fromConsultantSearchReducer.getIsIndustryComboLogicOR,
        ),
        this.store.select(fromConsultantSearchReducer.getUpdatedWithinMonths),
      ),
      debounceTime(200),
      switchMap(
        ([
          ,
          selectedSkills,
          selectedIndustryItems,
          selectedCapabilities,
          selectedCertifications,
          selectedOrganisations,
          allOrgs,
          selectedLocations,
          selectedRoles,
          anySearchTerm,
          consultantNameSearchTerm,
          showUnknownProficiency,
          includeRelatedSkills,
          includeExpiredCerts,
          isSkillComboLogicOR,
          isIndustryComboLogicOR,
          updatedWithinMonths,
        ]) => {
          const criteria = buildConsultantSearchCriteria({
            selectedSkills: selectedSkills ?? [],
            selectedIndustryItems: selectedIndustryItems ?? [],
            selectedCapabilities: selectedCapabilities ?? [],
            selectedCertifications: selectedCertifications ?? [],
            selectedOrganisations: selectedOrganisations ?? [],
            allOrgs: allOrgs ?? [],
            selectedLocations: selectedLocations ?? [],
            selectedRoles: selectedRoles ?? [],
            anySearchTerm: anySearchTerm ?? "",
            consultantNameSearchTerm: consultantNameSearchTerm ?? "",
            showUnknownProficiency: showUnknownProficiency ?? true,
            includeRelatedSkills: includeRelatedSkills ?? false,
            includeExpiredCerts: includeExpiredCerts ?? false,
            isSkillComboLogicOR: isSkillComboLogicOR ?? true,
            isIndustryComboLogicOR: isIndustryComboLogicOR ?? true,
            updatedWithinMonths: updatedWithinMonths ?? null,
            source: this.getCurrentSearchSource(),
          });

          const hasCriteria =
            (selectedSkills?.length ?? 0) > 0 ||
            (selectedIndustryItems?.length ?? 0) > 0 ||
            (selectedCapabilities?.length ?? 0) > 0 ||
            (selectedCertifications?.length ?? 0) > 0 ||
            (criteria.SelectedOrganisationIds?.length ?? 0) > 0 ||
            (selectedLocations?.length ?? 0) > 0 ||
            (selectedRoles?.length ?? 0) > 0 ||
            (anySearchTerm?.trim().length ?? 0) > 0 ||
            (consultantNameSearchTerm?.trim().length ?? 0) >= 3 ||
            (updatedWithinMonths !== null && updatedWithinMonths !== undefined);

          if (!hasCriteria) {
            this.lastFilteredConsultantsCriteriaKey = "";
            return of(
              SearchBySkillOrConsultantApiActions.filterConsultantsSuccess({
                consultants: [],
              }),
            );
          }

          const criteriaKey = buildCriteriaCacheKey(criteria);

          if (criteriaKey === this.lastFilteredConsultantsCriteriaKey) {
            return this.store
              .select(fromConsultantSearchReducer.getFilteredConsultants)
              .pipe(
                take(1),
                map((consultants) =>
                  SearchBySkillOrConsultantApiActions.filterConsultantsSuccess({
                    consultants: consultants ?? [],
                  }),
                ),
              );
          }

          this.lastFilteredConsultantsCriteriaKey = criteriaKey;

          return this.consultantSearchService
            .getFilteredConsultants(criteria)
            .pipe(
              map((consultants) =>
                SearchBySkillOrConsultantApiActions.filterConsultantsSuccess({
                  consultants,
                }),
              ),
              catchError((err: unknown) => {
                this.lastFilteredConsultantsCriteriaKey = "";

                const e = err as HttpErrorResponse;

                if (e?.status === 404) {
                  return of(
                    NotificationActions.snackbarOpen({
                      message: "No matching consultants found.",
                      action: "Dismiss",
                    }),
                    SearchBySkillOrConsultantApiActions.filterConsultantsSuccess(
                      {
                        consultants: [],
                      },
                    ),
                  );
                }

                return of(
                  SearchBySkillOrConsultantApiActions.filterConsultantsError(),
                  NotificationActions.snackbarOpen({
                    message: "Error: Unable to filter consultants.",
                    action: "Dismiss",
                  }),
                );
              }),
            );
        },
      ),
    ),
  );

  requestFilteredConsultantsCSV$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchBySkillOrConsultantApiActions.requestFilteredConsultantsCSV),
      withLatestFrom(
        this.store.select(fromConsultantSearchReducer.getSelectedSkills),
        this.store.select(fromConsultantSearchReducer.getSelectedIndustryItems),
        this.store.select(fromConsultantSearchReducer.getSelectedCapabilities),
        this.store.select(
          fromConsultantSearchReducer.getSelectedCertifications,
        ),
        this.store.select(fromConsultantSearchReducer.getSelectedOrgs),
        this.store.select(fromConsultantSearchReducer.getOrgs),
        this.store.select(fromConsultantSearchReducer.getSelectedLocations),
        this.store.select(fromConsultantSearchReducer.getSelectedRoles),
        this.store.select(fromConsultantSearchReducer.getAnySearchTerm),
        this.store.select(
          fromConsultantSearchReducer.getConsultantNameSearchTerm,
        ),
        this.store.select(
          fromConsultantSearchReducer.getShowUnknownProficiency,
        ),
        this.store.select(fromConsultantSearchReducer.getIncludeRelatedSkills),
        this.store.select(fromConsultantSearchReducer.getIncludeExpiredCerts),
        this.store.select(fromConsultantSearchReducer.getIsSkillComboLogicOR),
        this.store.select(
          fromConsultantSearchReducer.getIsIndustryComboLogicOR,
        ),
        this.store.select(fromConsultantSearchReducer.getUpdatedWithinMonths),
      ),
      switchMap(
        ([
          action,
          selectedSkills,
          selectedIndustryItems,
          selectedCapabilities,
          selectedCertifications,
          selectedOrganisations,
          allOrgs,
          selectedLocations,
          selectedRoles,
          anySearchTerm,
          consultantNameSearchTerm,
          showUnknownProficiency,
          includeRelatedSkills,
          includeExpiredCerts,
          isSkillComboLogicOR,
          isIndustryComboLogicOR,
          updatedWithinMonths,
        ]) => {
          const criteria = buildConsultantSearchCriteria({
            selectedSkills: selectedSkills ?? [],
            selectedIndustryItems: selectedIndustryItems ?? [],
            selectedCapabilities: selectedCapabilities ?? [],
            selectedCertifications: selectedCertifications ?? [],
            selectedOrganisations: selectedOrganisations ?? [],
            allOrgs: allOrgs ?? [],
            selectedLocations: selectedLocations ?? [],
            selectedRoles: selectedRoles ?? [],
            anySearchTerm: anySearchTerm ?? "",
            consultantNameSearchTerm: consultantNameSearchTerm ?? "",
            showUnknownProficiency: showUnknownProficiency ?? true,
            includeRelatedSkills: includeRelatedSkills ?? false,
            includeExpiredCerts: includeExpiredCerts ?? false,
            isSkillComboLogicOR: isSkillComboLogicOR ?? true,
            isIndustryComboLogicOR: isIndustryComboLogicOR ?? true,
            updatedWithinMonths: updatedWithinMonths ?? null,
            source: this.getCurrentSearchSource(),
          });

          const hasCriteria =
            (selectedSkills?.length ?? 0) > 0 ||
            (selectedIndustryItems?.length ?? 0) > 0 ||
            (selectedCapabilities?.length ?? 0) > 0 ||
            (selectedCertifications?.length ?? 0) > 0 ||
            (criteria.SelectedOrganisationIds?.length ?? 0) > 0 ||
            (selectedLocations?.length ?? 0) > 0 ||
            (selectedRoles?.length ?? 0) > 0 ||
            (anySearchTerm?.trim().length ?? 0) > 0 ||
            (consultantNameSearchTerm?.trim().length ?? 0) >= 3 ||
            (updatedWithinMonths !== null && updatedWithinMonths !== undefined);

          if (!hasCriteria) {
            return of(
              SearchBySkillOrConsultantApiActions.filteredConsultantsCSVError(),
              NotificationActions.snackbarOpen({
                message:
                  "Please set at least one filter before downloading CSV.",
                action: "Dismiss",
              }),
            );
          }

          return this.consultantSearchService
            .createCSVFromFilter(criteria, action.includeSkills)
            .pipe(
              map((response) => {
                const filename =
                  tryExtractFilenameFromContentDisposition(
                    response.headers.get("content-disposition"),
                  ) ?? "SearchResults.csv";

                const body = response.body;
                const blob =
                  body instanceof Blob
                    ? body
                    : new Blob([body as unknown as BlobPart], {
                        type: "text/csv;charset=utf-8",
                      });

                saveAs(blob, filename, { autoBom: false });

                return SearchBySkillOrConsultantApiActions.filteredConsultantsCSVSuccess();
              }),
              catchError(() =>
                of(
                  SearchBySkillOrConsultantApiActions.filteredConsultantsCSVError(),
                  NotificationActions.snackbarOpen({
                    message: "Error: Unable to generate CSV.",
                    action: "Dismiss",
                  }),
                ),
              ),
            );
        },
      ),
    ),
  );
}
